import React, { useCallback, useEffect, useRef, useState } from 'react'

import {
	ArrowLeft,
	Loader2,
	CalendarIcon,
	Upload,
	Trash2,
	Pencil,
	Play,
	Pause,
	X,
} from 'lucide-react'
import { DateTime } from 'luxon'
import type { UseFormReturn } from 'react-hook-form'
import { useSelector } from 'react-redux'

import coverImageBlack from '@/assets/images/logo_isotipo_black.png'
import coverImageWhite from '@/assets/images/logo_isotipo_white.png'
import type { Advert } from '@/core/api/entities/campaign/advert'
import { usePermission } from '@/core/hooks/usePermission'
import { useTranslation } from '@/core/i18n/useTranslation'
import type { RootState } from '@/core/store'
import {
	resetChatbaseLauncherBottomOffset,
	setChatbaseLauncherBottomOffset,
} from '@/shared/analytics/ChatbaseWidget'
import { Button } from '@/shared/components/atoms/Button'
import {
	Form,
	FormField,
	FormItem,
	FormLabel,
	FormControl,
	FormMessage,
} from '@/shared/components/atoms/Form'
import Icon from '@/shared/components/atoms/Icon'
import { Input } from '@/shared/components/atoms/Input'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/shared/components/atoms/Popover'
import { Separator } from '@/shared/components/atoms/Separator'
import { Textarea } from '@/shared/components/atoms/TextArea'
import { Calendar } from '@/shared/components/organisms/Calendar/Calendar'
import AuthenticatedLayout from '@/shared/components/organisms/Layout/AuthenticatedLayout/AuthenticatedLayout'
import { DesktopVolumeControl } from '@/shared/components/organisms/Player/DesktopVolumeControl'
import Player from '@/shared/components/organisms/Player/Player'
import { cn } from '@/shared/utils/helpers'

import { EditAdvertModal } from './components/EditAdvertModal'
import { SelectExistingAdModal } from './components/SelectExistingAdModal'
import type {
	UploadAdFormValues,
	PendingAdvertItem,
	EditAdvertFormValues,
} from './schemas/createCampaignSchema'

interface CampaignDetailsStep2UIProps {
	title: string
	form: UseFormReturn<UploadAdFormValues>
	adverts: Advert[]
	pendingAdverts: PendingAdvertItem[]
	selectedAdvertIds: number[]
	isSubmitting: boolean
	isAddExistingAdModalOpen: boolean
	onOpenChangeExistingAdModal: (open: boolean) => void
	onExistingAdd: () => void
	onConfirmExistingAds: (selectedAdverts: Advert[]) => Promise<void>
	onSubmit: (values: UploadAdFormValues) => Promise<void>
	onBack: () => void
	onCancel: () => void
	onRemoveAdvert: (advertId: number) => void
	onAddCurrentToPending: () => void
	onRemovePendingAdvert: (index: number) => void
	onMultipleFilesAttempt: () => void
	advertBeingEdited: Advert | null
	isEditingSaving: boolean
	onEditAdvert: (advert: Advert) => void
	onOpenChangeEditAdModal: (open: boolean) => void
	onSaveEditedAdvert: (advertId: number, values: EditAdvertFormValues) => void
}

export const CampaignDetailsStep2UI: React.FC<CampaignDetailsStep2UIProps> = ({
	title,
	form,
	adverts,
	pendingAdverts,
	selectedAdvertIds,
	isSubmitting,
	isAddExistingAdModalOpen,
	onOpenChangeExistingAdModal,
	onExistingAdd,
	onConfirmExistingAds,
	onSubmit,
	onBack,
	onCancel,
	onRemoveAdvert,
	onAddCurrentToPending,
	onRemovePendingAdvert,
	onMultipleFilesAttempt,
	advertBeingEdited,
	isEditingSaving,
	onEditAdvert,
	onOpenChangeEditAdModal,
	onSaveEditedAdvert,
}) => {
	const { t } = useTranslation('campaigns')
	const theme = useSelector<RootState, RootState['ui']['theme']>(
		(state) => state.ui.theme
	)
	const playerCoverImage = theme === 'light' ? coverImageBlack : coverImageWhite
	const { hasAnyPermission } = usePermission()
	const canEditAdverts = hasAnyPermission([
		'update_adverts',
		'update_own_adverts',
		'manage_children_announcements',
	])
	const [mobileUploadMode, setMobileUploadMode] = useState<'new' | 'existing'>(
		'new'
	)
	const [activeAdvertId, setActiveAdvertId] = useState<number | null>(null)
	const [currentAdvert, setCurrentAdvert] = useState<Advert | null>(null)
	const [isPlayerPlaying, setIsPlayerPlaying] = useState(false)
	const audioElementRef = useRef<HTMLAudioElement | null>(null)
	const [isMuted, setIsMuted] = useState(false)
	const [volume, setVolume] = useState(1)
	const [isPlayerBarMounted, setIsPlayerBarMounted] = useState(false)
	const [isPlayerBarVisible, setIsPlayerBarVisible] = useState(false)
	const [displayedAdvert, setDisplayedAdvert] = useState<Advert | null>(null)

	const handleAudioRef = useCallback((element: HTMLAudioElement | null) => {
		audioElementRef.current = element
	}, [])

	useEffect(() => {
		if (!isAddExistingAdModalOpen && mobileUploadMode === 'existing') {
			setMobileUploadMode('new')
		}
	}, [isAddExistingAdModalOpen, mobileUploadMode])

	useEffect(() => {
		let rafId: number | null = null
		if (currentAdvert) {
			setDisplayedAdvert(currentAdvert)
			setIsPlayerBarMounted(true)
			rafId = window.requestAnimationFrame(() => setIsPlayerBarVisible(true))
			return () => {
				if (rafId !== null) {
					window.cancelAnimationFrame(rafId)
				}
			}
		}

		setIsPlayerBarVisible(false)
		const timerId = window.setTimeout(() => {
			setIsPlayerBarMounted(false)
			setDisplayedAdvert(null)
		}, 300)
		return () => window.clearTimeout(timerId)
	}, [currentAdvert])

	useEffect(() => {
		if (currentAdvert) {
			setChatbaseLauncherBottomOffset(140)
			const started = Date.now()
			const intervalId = window.setInterval(() => {
				setChatbaseLauncherBottomOffset(140)
				if (Date.now() - started > 3000) window.clearInterval(intervalId)
			}, 250)
			return () => {
				window.clearInterval(intervalId)
				resetChatbaseLauncherBottomOffset()
			}
		}

		resetChatbaseLauncherBottomOffset()
	}, [currentAdvert])

	useEffect(() => {
		if (!currentAdvert) return
		const stillExists = adverts.some((advert) => advert.id === currentAdvert.id)
		if (!stillExists) {
			audioElementRef.current?.pause()
			setCurrentAdvert(null)
			setActiveAdvertId(null)
			setIsPlayerPlaying(false)
		}
	}, [adverts, currentAdvert])

	useEffect(() => {
		const audioElement = audioElementRef.current
		if (!audioElement) return

		audioElement.volume = isMuted ? 0 : volume
	}, [isMuted, volume])

	useEffect(() => {
		const audioElement = audioElementRef.current
		const isCurrentAdvertDisplayed =
			currentAdvert?.id !== undefined &&
			displayedAdvert?.id !== undefined &&
			currentAdvert.id === displayedAdvert.id

		if (!audioElement || !isCurrentAdvertDisplayed) return

		if (isPlayerPlaying && audioElement.paused) {
			audioElement.play().catch(() => {})
		}
	}, [currentAdvert?.id, displayedAdvert?.id, isPlayerPlaying])

	const handleAdvertRowClick = (advert: Advert) => {
		const isCurrentAdvert = currentAdvert?.id === advert.id
		if (isCurrentAdvert) {
			const audioElement = audioElementRef.current
			if (audioElement?.paused) {
				audioElement.play().catch(() => {})
			} else {
				audioElement?.pause()
			}
			return
		}
		setActiveAdvertId(advert.id)
		setCurrentAdvert(advert)
		setIsPlayerPlaying(true)
	}

	const handleClosePlayer = () => {
		audioElementRef.current?.pause()
		setCurrentAdvert(null)
		setActiveAdvertId(null)
		setIsPlayerPlaying(false)
	}

	const renderAdvertDateRange = (advert: Advert) => {
		const startLabel = advert.dateStart
			? advert.dateStart.toFormat('MMM d, yyyy')
			: t('upload.noStart')
		const endLabel = advert.dateEnd
			? advert.dateEnd.toFormat('MMM d, yyyy')
			: t('upload.noEnd')
		return `${startLabel} – ${endLabel}`
	}

	const renderPlayButton = (advert: Advert) => {
		const isCurrent = currentAdvert?.id === advert.id
		if (isCurrent && isPlayerPlaying) {
			return (
				<Button
					variant="ghost"
					size="icon"
					className="h-8 w-8 hover:bg-accent shrink-0"
					onClick={(event) => {
						event.stopPropagation()
						audioElementRef.current?.pause()
					}}
				>
					<Pause className="h-4 w-4" />
				</Button>
			)
		}

		return (
			<Button
				variant="ghost"
				size="icon"
				className="h-8 w-8 hover:bg-accent shrink-0"
				onClick={(event) => {
					event.stopPropagation()
					if (isCurrent) {
						audioElementRef.current?.play().catch(() => {})
						setIsPlayerPlaying(true)
						return
					}
					setActiveAdvertId(advert.id)
					setCurrentAdvert(advert)
					setIsPlayerPlaying(true)
				}}
			>
				<Play className="h-4 w-4" />
			</Button>
		)
	}

	const pickFirstValidMp3 = (files: File[]): File | undefined =>
		files.find((file) => file.type === 'audio/mpeg')

	const hasCurrentUpload = Boolean(form.watch('audioFile'))
	const handlePrimaryAddClick = (
		event: React.MouseEvent<HTMLButtonElement>
	) => {
		event.preventDefault()
		onAddCurrentToPending()
	}

	return (
		<AuthenticatedLayout requireAuth={true}>
			<div className="md:hidden fixed inset-0 z-40 bg-black/30 flex items-end">
				<div className="w-full">
					<div
						className={cn(
							'bg-card w-full h-[92dvh] rounded-t-2xl border-t border-border overflow-hidden flex flex-col min-h-0',
							isPlayerBarMounted && 'pb-[60px]'
						)}
					>
						<div className="flex py-3 px-4 items-center justify-between">
							<h2 className="text-lg font-semibold tracking-tight">{title}</h2>
							<div className="flex items-center gap-2">
								<div className="rounded-lg bg-muted px-2.5 py-1 text-base font-medium">
									2/2
								</div>
								<button
									type="button"
									onClick={onBack}
									className="h-8 w-8 flex items-center justify-center rounded-full hover:bg-muted/80 transition-colors"
								>
									<X className="w-5 h-5" />
								</button>
							</div>
						</div>

						<Separator />

						<Form {...form}>
							<form
								id="campaign-upload-mobile-form"
								onSubmit={form.handleSubmit(onSubmit)}
								className={cn(
									'flex-1 overflow-y-auto px-4 py-4 space-y-5 pb-28',
									isPlayerBarMounted &&
										'pb-[calc(5rem+env(safe-area-inset-bottom))]'
								)}
							>
								<div className="bg-background rounded-full p-0.5 grid grid-cols-2 gap-0.5">
									<Button
										type="button"
										variant="ghost"
										className={`rounded-full h-10 text-sm px-2 min-w-0 ${
											mobileUploadMode === 'new'
												? 'bg-outline text-foreground'
												: 'text-muted-foreground'
										}`}
										onClick={() => setMobileUploadMode('new')}
									>
										<span className="truncate">
											{t('upload.mobile_new_ad')}
										</span>
									</Button>
									<Button
										type="button"
										variant="ghost"
										className={`rounded-full h-10 text-sm px-2 min-w-0 ${
											mobileUploadMode === 'existing'
												? 'bg-outline text-foreground'
												: 'text-muted-foreground'
										}`}
										onClick={() => {
											setMobileUploadMode('existing')
											onExistingAdd()
										}}
									>
										<span className="truncate">
											{t('upload.mobile_select_ad')}
										</span>
									</Button>
								</div>

								<FormField
									control={form.control}
									name="advertName"
									render={({ field }) => (
										<FormItem>
											<FormLabel>{t('upload.advertName')}</FormLabel>
											<FormControl>
												<Input
													placeholder={t('upload.advertNamePlaceholder')}
													{...field}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>

								<FormField
									control={form.control}
									name="description"
									render={({ field }) => (
										<FormItem>
											<FormLabel>{t('upload.description')}</FormLabel>
											<FormControl>
												<Textarea
													placeholder={t('upload.descriptionPlaceholder')}
													{...field}
													value={field.value ?? ''}
													className="min-h-[100px] bg-background border-border text-foreground resize-none"
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>

								<div className="grid grid-cols-2 gap-3">
									<FormField
										control={form.control}
										name="adStartDate"
										render={({ field }) => (
											<FormItem>
												<FormLabel>{t('upload.adStartDateMobile')}</FormLabel>
												<Popover>
													<PopoverTrigger asChild>
														<FormControl>
															<Button
																variant="secondary"
																className="w-full justify-start font-normal"
															>
																<CalendarIcon className="mr-2 h-4 w-4" />
																{field.value ? (
																	DateTime.fromJSDate(field.value).toFormat(
																		'EEE d LLL, yyyy'
																	)
																) : (
																	<span>{t('upload.pickDate')}</span>
																)}
															</Button>
														</FormControl>
													</PopoverTrigger>
													<PopoverContent className="w-auto p-0">
														<Calendar
															mode="single"
															selected={field.value ?? undefined}
															onSelect={field.onChange}
														/>
													</PopoverContent>
												</Popover>
												<FormMessage />
											</FormItem>
										)}
									/>

									<FormField
										control={form.control}
										name="adEndDate"
										render={({ field }) => (
											<FormItem>
												<FormLabel>{t('upload.adEndDateMobile')}</FormLabel>
												<Popover>
													<PopoverTrigger asChild>
														<FormControl>
															<Button
																variant="secondary"
																className="w-full justify-start font-normal"
															>
																<CalendarIcon className="mr-2 h-4 w-4" />
																{field.value ? (
																	DateTime.fromJSDate(field.value).toFormat(
																		'EEE d LLL, yyyy'
																	)
																) : (
																	<span>{t('upload.pickDate')}</span>
																)}
															</Button>
														</FormControl>
													</PopoverTrigger>
													<PopoverContent className="w-auto p-0">
														<Calendar
															mode="single"
															selected={field.value ?? undefined}
															onSelect={field.onChange}
														/>
													</PopoverContent>
												</Popover>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>

								<FormField
									control={form.control}
									name="audioFile"
									render={({ field: { value, onChange, ...field } }) => (
										<FormItem>
											<FormLabel>{t('upload.audioFile')}</FormLabel>
											<div
												className="border border-dashed border-border rounded-2xl px-4 py-3 cursor-pointer"
												onClick={() =>
													document.getElementById('mobile-file-upload')?.click()
												}
											>
												<div className="flex items-start gap-3">
													<div className="rounded-md border border-border p-2">
														<Upload className="w-4 h-4" />
													</div>
													<div className="flex-1 min-w-0">
														<p className="font-medium">
															{t('upload.uploadNew')}
														</p>
														<p className="text-xs text-muted-foreground">
															{t('upload.onlyMp3')} ·{' '}
															{t('upload.multipleFiles', {
																defaultValue: 'one file at a time',
															})}
														</p>
														{value && (
															<p className="text-xs mt-1 truncate">
																{value.name}
															</p>
														)}
													</div>
												</div>
											</div>
											<FormControl>
												<input
													{...field}
													id="mobile-file-upload"
													type="file"
													accept=".mp3,audio/mpeg"
													className="hidden"
													onChange={(e) => {
														const files = Array.from(e.target.files || [])
														e.target.value = ''
														if (files.length === 0) return
														if (files.length > 1) onMultipleFilesAttempt()
														const firstValidMp3 = pickFirstValidMp3(files)
														if (firstValidMp3) onChange(firstValidMp3)
													}}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>

								{pendingAdverts.length > 0 && (
									<div className="space-y-2">
										<h4 className="text-sm font-medium">
											{t('upload.pendingAds', {
												defaultValue: 'Ads to upload',
											})}{' '}
											({pendingAdverts.length})
										</h4>
										<ul className="space-y-1.5">
											{pendingAdverts.map((item, index) => (
												<li
													key={`${item.audioFile.name}-${index}`}
													className="flex items-center justify-between rounded-lg border px-3 py-2 bg-muted/50 text-sm"
												>
													<span className="truncate flex-1">
														{item.advertName}
													</span>
													<Button
														type="button"
														variant="ghost"
														size="icon"
														className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
														onClick={() => onRemovePendingAdvert(index)}
													>
														<Trash2 className="h-4 w-4" />
													</Button>
												</li>
											))}
										</ul>
									</div>
								)}

								<div className="pt-1">
									<h3 className="text-xl md:text-3xl font-medium mb-3">
										{t('upload.uploaded_ads')}
									</h3>
									{adverts && adverts.length > 0 ? (
										<ul className="divide-y text-sm border rounded-md bg-muted/50 overflow-hidden">
											{adverts.map((advert) => (
												<li
													key={advert.id}
													className={cn(
														'flex items-center justify-between px-3 py-2 bg-card gap-2',
														activeAdvertId === advert.id && 'bg-accent/20'
													)}
												>
													<button
														type="button"
														className="flex items-center gap-3 min-w-0 text-left flex-1"
														onClick={() => handleAdvertRowClick(advert)}
													>
														{renderPlayButton(advert)}
														<div className="flex flex-col min-w-0">
															<span className="font-medium truncate max-w-[200px]">
																{advert.name}
															</span>
															<span className="text-[11px] text-muted-foreground">
																{renderAdvertDateRange(advert)}
															</span>
														</div>
													</button>
													<div className="flex items-center gap-0.5 shrink-0">
														<span className="text-[11px] text-muted-foreground">
															ID {advert.id}
														</span>
														{canEditAdverts && (
															<Button
																type="button"
																variant="ghost"
																size="icon"
																className="h-7 w-7 text-muted-foreground hover:text-foreground"
																onClick={() => onEditAdvert(advert)}
															>
																<Pencil className="h-3.5 w-3.5" />
															</Button>
														)}
														<Button
															type="button"
															variant="ghost"
															size="icon"
															className="h-7 w-7 text-muted-foreground hover:text-destructive"
															onClick={() => onRemoveAdvert(advert.id)}
														>
															<Trash2 className="h-4 w-4" />
														</Button>
													</div>
												</li>
											))}
										</ul>
									) : (
										<p className="text-xs text-muted-foreground">
											{t('upload.noAdsAssigned')}
										</p>
									)}
								</div>
							</form>
						</Form>

						<div
							className={cn(
								'h-24 flex items-center justify-center px-4 pb-[calc(env(safe-area-inset-bottom)+12px)] pt-3 border-t border-border bg-card',
								isPlayerBarMounted && 'mb-[60px]'
							)}
						>
							<Button
								type={hasCurrentUpload ? 'button' : 'submit'}
								form={
									hasCurrentUpload ? undefined : 'campaign-upload-mobile-form'
								}
								className="rounded-full w-full"
								onClick={hasCurrentUpload ? handlePrimaryAddClick : undefined}
								disabled={
									isSubmitting ||
									(!hasCurrentUpload &&
										pendingAdverts.length === 0 &&
										adverts.length === 0)
								}
							>
								{isSubmitting ? (
									<>
										<Loader2 className="w-4 h-4 mr-2 animate-spin" />
										{t('upload.creating')}
									</>
								) : hasCurrentUpload ? (
									t('upload.addToList', { defaultValue: 'Add to list' })
								) : (
									t('upload.continue')
								)}
							</Button>
						</div>
					</div>
				</div>
			</div>

			<div
				className={cn(
					'hidden md:block w-full pl-3 pr-3 2xl:pl-6 2xl:pr-6 py-3 2xl:py-6',
					isPlayerBarMounted && 'pb-[72px]'
				)}
			>
				<div className="max-w-[1400px] mx-auto w-full">
					<div
						className={cn(
							'border rounded-xl bg-card',
							isPlayerBarMounted && 'pb-[96px] md:pb-[120px] lg:pb-[132px]'
						)}
					>
						<div className="flex py-6 px-8 items-center gap-4">
							<button
								onClick={onBack}
								className="flex items-center justify-center"
							>
								<ArrowLeft className="w-6 h-6" />
							</button>
							<h2 className="text-lg font-normal tracking-tight md:text-3xl">
								{title}
							</h2>
						</div>

						<Separator />

						<div
							className={cn(
								'px-8 py-6 flex-1 flex gap-8',
								isPlayerBarMounted && 'pb-[72px]'
							)}
						>
							<div className="flex-1">
								<div className="flex flex-row items-center justify-between">
									<p>{t('upload.uploadNew')}</p>
									<Button
										variant="secondary"
										className="justify-start font-normal rounded-full"
										onClick={onExistingAdd}
									>
										{t('upload.selectExisting')}
									</Button>
								</div>

								<Form {...form}>
									<form
										onSubmit={form.handleSubmit(onSubmit)}
										className={cn(
											'space-y-6 mt-4',
											isPlayerBarMounted && 'pb-14'
										)}
									>
										<FormField
											control={form.control}
											name="advertName"
											render={({ field }) => (
												<FormItem>
													<FormLabel>{t('upload.advertName')}</FormLabel>
													<FormControl>
														<Input
															placeholder={t('upload.advertNamePlaceholder')}
															{...field}
														/>
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>

										<FormField
											control={form.control}
											name="description"
											render={({ field }) => (
												<FormItem>
													<FormLabel>{t('upload.description')}</FormLabel>
													<FormControl>
														<Textarea
															placeholder={t('upload.descriptionPlaceholder')}
															{...field}
															value={field.value ?? ''}
															className="min-h-[100px] bg-background border-border text-foreground resize-none"
														/>
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>

										<div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
											<FormField
												control={form.control}
												name="adStartDate"
												render={({ field }) => (
													<FormItem>
														<FormLabel>{t('upload.adStartDate')}</FormLabel>
														<Popover>
															<PopoverTrigger asChild>
																<FormControl>
																	<Button
																		variant="secondary"
																		className="w-full justify-start font-normal"
																	>
																		<CalendarIcon className="mr-2 h-4 w-4" />
																		{field.value ? (
																			DateTime.fromJSDate(field.value).toFormat(
																				'EEE d LLL, yyyy'
																			)
																		) : (
																			<span>{t('upload.pickDate')}</span>
																		)}
																	</Button>
																</FormControl>
															</PopoverTrigger>
															<PopoverContent className="w-auto p-0">
																<Calendar
																	mode="single"
																	selected={field.value ?? undefined}
																	onSelect={field.onChange}
																/>
															</PopoverContent>
														</Popover>
														<FormMessage />
													</FormItem>
												)}
											/>

											<FormField
												control={form.control}
												name="adEndDate"
												render={({ field }) => (
													<FormItem>
														<FormLabel>{t('upload.adEndDate')}</FormLabel>
														<Popover>
															<PopoverTrigger asChild>
																<FormControl>
																	<Button
																		variant="secondary"
																		className="w-full justify-start font-normal"
																	>
																		<CalendarIcon className="mr-2 h-4 w-4" />
																		{field.value ? (
																			DateTime.fromJSDate(field.value).toFormat(
																				'EEE d LLL, yyyy'
																			)
																		) : (
																			<span>{t('upload.pickDate')}</span>
																		)}
																	</Button>
																</FormControl>
															</PopoverTrigger>
															<PopoverContent className="w-auto p-0">
																<Calendar
																	mode="single"
																	selected={field.value ?? undefined}
																	onSelect={field.onChange}
																/>
															</PopoverContent>
														</Popover>
														<FormMessage />
													</FormItem>
												)}
											/>
										</div>

										<FormField
											control={form.control}
											name="audioFile"
											render={({ field: { value, onChange, ...field } }) => (
												<FormItem>
													<FormLabel>{t('upload.audioFile')}</FormLabel>
													<div>
														<div
															className="border-2 border-dashed border-border rounded-lg p-12 text-center hover:border-primary transition-colors cursor-pointer"
															onDragOver={(e) => e.preventDefault()}
															onDrop={(e) => {
																e.preventDefault()
																const files = Array.from(e.dataTransfer.files)
																if (files.length > 1) onMultipleFilesAttempt()
																const firstValidMp3 = pickFirstValidMp3(files)
																if (firstValidMp3) onChange(firstValidMp3)
															}}
															onClick={() =>
																document
																	.getElementById('desktop-file-upload')
																	?.click()
															}
														>
															<Upload className="w-8 h-8 mx-auto mb-4 text-muted-foreground" />
															<p className="text-base mb-2">
																{t('upload.dragDrop')}{' '}
																<span className="underline">
																	{t('upload.chooseFile')}
																</span>
															</p>
															<p className="text-sm text-muted-foreground">
																{t('upload.onlyMp3')} ·{' '}
																{t('upload.multipleFiles', {
																	defaultValue: 'one file at a time',
																})}
															</p>

															{value && (
																<div className="mt-4 p-3 bg-muted rounded-md inline-block">
																	<p className="text-sm font-medium">
																		{value.name}
																	</p>
																	<p className="text-xs text-muted-foreground">
																		{(value.size / 1024 / 1024).toFixed(2)} MB
																	</p>
																</div>
															)}
														</div>
														<FormControl>
															<input
																{...field}
																id="desktop-file-upload"
																type="file"
																accept=".mp3,audio/mpeg"
																className="hidden"
																onChange={(e) => {
																	const files = Array.from(e.target.files || [])
																	e.target.value = ''
																	if (files.length === 0) return
																	if (files.length > 1) onMultipleFilesAttempt()
																	const firstValidMp3 = pickFirstValidMp3(files)
																	if (firstValidMp3) onChange(firstValidMp3)
																}}
															/>
														</FormControl>
													</div>
													<FormMessage />
												</FormItem>
											)}
										/>

										{pendingAdverts.length > 0 && (
											<div className="space-y-2">
												<h4 className="text-sm font-medium">
													{t('upload.pendingAds', {
														defaultValue: 'Ads to upload',
													})}{' '}
													({pendingAdverts.length})
												</h4>
												<ul className="space-y-2">
													{pendingAdverts.map((item, index) => (
														<li
															key={`${item.audioFile.name}-${index}`}
															className="flex items-center justify-between rounded-lg border px-3 py-2 bg-muted/50 text-sm"
														>
															<span className="truncate flex-1">
																{item.advertName}
															</span>
															<Button
																type="button"
																variant="ghost"
																size="icon"
																className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
																onClick={() => onRemovePendingAdvert(index)}
															>
																<Trash2 className="h-4 w-4" />
															</Button>
														</li>
													))}
												</ul>
											</div>
										)}

										<div className="flex justify-between pt-6">
											<Button
												type="button"
												variant="outline"
												onClick={onCancel}
												disabled={isSubmitting}
											>
												{t('upload.cancel')}
											</Button>
											<Button
												type={hasCurrentUpload ? 'button' : 'submit'}
												onClick={
													hasCurrentUpload ? handlePrimaryAddClick : undefined
												}
												disabled={
													isSubmitting ||
													(!hasCurrentUpload &&
														pendingAdverts.length === 0 &&
														adverts.length === 0)
												}
											>
												{isSubmitting ? (
													<>
														<Loader2 className="w-4 h-4 mr-2 animate-spin" />
														{t('upload.creating')}
													</>
												) : hasCurrentUpload ? (
													t('upload.addToList', { defaultValue: 'Add to list' })
												) : (
													t('upload.continue')
												)}
											</Button>
										</div>
									</form>
								</Form>
							</div>

							{adverts && adverts.length > 0 && (
								<div className="hidden lg:block w-[380px] border-l pl-8">
									<h3 className="text-sm font-medium mb-4">
										{t('upload.selectedAds')}
									</h3>
									<ul className="divide-y text-sm max-h-[90vh] md:border md:rounded-md pr-2 md:pr-0 md:bg-muted/50 overflow-y-auto">
										{adverts.map((advert) => (
											<li
												key={advert.id}
												className={cn(
													'flex items-center justify-between px-3 py-2 bg-card gap-2',
													activeAdvertId === advert.id && 'bg-accent/20'
												)}
											>
												<button
													type="button"
													className="flex items-center gap-3 min-w-0 text-left flex-1"
													onClick={() => handleAdvertRowClick(advert)}
												>
													{renderPlayButton(advert)}
													<div className="flex flex-col min-w-0">
														<span className="font-medium truncate max-w-[220px]">
															{advert.name}
														</span>
														<span className="text-[11px] text-muted-foreground">
															{renderAdvertDateRange(advert)}
														</span>
													</div>
												</button>
												<div className="flex items-center gap-0.5">
													<span className="text-[11px] text-muted-foreground">
														ID {advert.id}
													</span>
													{canEditAdverts && (
														<Button
															variant="ghost"
															size="icon"
															className="h-8 w-8 text-muted-foreground hover:text-foreground"
															onClick={() => onEditAdvert(advert)}
														>
															<Pencil className="h-3.5 w-3.5" />
														</Button>
													)}
													<Button
														variant="ghost"
														size="icon"
														className="h-8 w-8 text-muted-foreground hover:text-destructive"
														onClick={() => onRemoveAdvert(advert.id)}
													>
														<Trash2 className="h-4 w-4" />
													</Button>
												</div>
											</li>
										))}
									</ul>
								</div>
							)}

							{(!adverts || adverts.length === 0) && (
								<div className="w-[380px] border-l pl-8">
									<h3 className="text-sm font-medium mb-4">
										{t('upload.selectedAds')}
									</h3>
									<p className="text-xs text-muted-foreground">
										{t('upload.noAdsAssigned')}
									</p>
								</div>
							)}
						</div>
					</div>
				</div>
			</div>

			<SelectExistingAdModal
				open={isAddExistingAdModalOpen}
				onOpenChange={onOpenChangeExistingAdModal}
				selectedAdvertIds={selectedAdvertIds}
				onConfirmSelection={onConfirmExistingAds}
			/>

			<EditAdvertModal
				open={advertBeingEdited !== null}
				advert={advertBeingEdited}
				isSaving={isEditingSaving}
				onOpenChange={onOpenChangeEditAdModal}
				onSave={onSaveEditedAdvert}
			/>
			{isPlayerBarMounted && displayedAdvert && (
				<div
					className={cn(
						'fixed bottom-0 md:bottom-0 left-0 right-0 lg:left-20 z-[1000] transition-transform duration-300 ease-out',
						isPlayerBarVisible ? 'translate-y-0' : 'translate-y-full'
					)}
				>
					<div className="w-full 2xl:px-6 md:px-3">
						<div className="mx-auto w-full max-w-[1400px]">
							<div className="player-mobile-shell dark:md:bg-card md:border md:bg-secondary lg:rounded-b-[8px] px-4 py-3 md:px-6 md:py-4 lg:py-6 flex items-center lg:h-[120px]">
								<div className="w-full flex items-center gap-4">
									<Player
										key={displayedAdvert.id}
										src={displayedAdvert.path}
										cover={playerCoverImage}
										title={displayedAdvert.name || ''}
										metadata={[title].filter(Boolean)}
										autoPlay={true}
										onPlay={() => setIsPlayerPlaying(true)}
										onPause={() => setIsPlayerPlaying(false)}
										onEnded={() => setIsPlayerPlaying(false)}
										hideVolume={true}
										compact={true}
										showSkipControls={false}
										audioRef={handleAudioRef}
										cssVars={{
											border: 'none',
											backgroundColor: 'transparent',
										}}
									/>

									<div className="lg:flex hidden items-center gap-6 shrink-0">
										<DesktopVolumeControl
											isMuted={isMuted}
											setIsMuted={setIsMuted}
											volume={volume}
											setVolume={setVolume}
											audioElementRef={audioElementRef}
										/>
										<div aria-hidden="true" className="w-[56px] shrink-0" />
									</div>
									<button
										className="absolute top-1 right-1 md:top-2 md:right-4 rounded-[9999px] cursor-pointer p-2 flex items-center justify-center shrink-0"
										onClick={handleClosePlayer}
										aria-label={t('common:actions.close', 'Close')}
									>
										<Icon
											name="close"
											width="16px"
											height="16px"
											color="var(--foreground)"
										/>
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>
			)}
		</AuthenticatedLayout>
	)
}

export default CampaignDetailsStep2UI
